/**
 * InstallPwaBanner — convida o usuário a instalar o PWA no celular/desktop.
 *
 * R6.5 / FRENTE C (2026-05-25).
 *
 * Comportamento Android/Chrome/Edge:
 *   - Navegador dispara `beforeinstallprompt` quando heurísticas batem
 *     (HTTPS + manifest + SW + algum engajamento).
 *   - Salvamos o event, renderizamos banner "Instalar IGM como app".
 *   - Click "Instalar" → `installEvent.prompt()` → dialog nativo do
 *     navegador → user aceita/recusa.
 *
 * iOS NÃO suporta `beforeinstallprompt` (decisão da Apple). Detectamos
 * iOS Safari + não-standalone e mostramos instruções manuais:
 *   "Toque em 📤 → Adicionar à Tela de Início"
 *
 * Dismiss persiste em sessionStorage (não localStorage — UI cosmetic,
 * sem state de negócio; restrição 5 do briefing). User dismissou →
 * banner some até refresh da aba.
 *
 * Skip quando:
 *   - Já instalado (display-mode: standalone ou navigator.standalone).
 *   - User dismissou nessa sessão.
 *   - Navegador não disparou beforeinstallprompt + não é iOS Safari.
 *
 * NÃO REVERTER:
 *   - sessionStorage (não localStorage) — restrição 5.
 *   - iOS detection precisa do navigator.standalone (Safari only).
 *   - preventDefault em beforeinstallprompt é OBRIGATÓRIO senão Chrome
 *     mostra banner próprio random.
 */

import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';

const DISMISSED_KEY = 'pwa-install-dismissed';

function isStandaloneMode(): boolean {
  // Android/Chrome/Edge/Firefox.
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari (proprietary).
  if ((window.navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
  return false;
}

function isIOSDevice(): boolean {
  const ua = window.navigator.userAgent;
  // iPadOS 13+ identifica como Mac — extra check via touch.
  const isIPad = /Mac/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || isIPad;
}

function isIOSSafari(): boolean {
  if (!isIOSDevice()) return false;
  const ua = window.navigator.userAgent;
  // Exclui Chrome iOS (CriOS), Firefox iOS (FxiOS), etc — todos usam
  // WebKit mas só Safari original suporta "Add to Home Screen".
  return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function InstallPwaBanner() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = (e: BeforeInstallPromptEvent) => {
      // CRÍTICO: sem preventDefault, Chrome mostra mini-infobar próprio.
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // sessionStorage pode falhar em modo privado — fail silent.
    }
  };

  const handleInstall = async () => {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      // Independente do outcome (accepted/dismissed), o event não pode
      // ser reusado — limpar pra esconder banner.
      await installEvent.userChoice;
    } catch (err) {
      console.error('[PWA] install prompt error:', err);
    } finally {
      setInstallEvent(null);
    }
  };

  // Skip condições — ordem importa.
  if (dismissed) return null;
  if (isStandaloneMode()) return null;

  // iOS Safari: instruções manuais (sem beforeinstallprompt support).
  if (isIOSSafari()) {
    return (
      <div
        role="region"
        aria-label="Instalar IGM"
        data-testid="pwa-install-banner-ios"
        className="rounded-xl border border-line dark:border-line-dark bg-paper-warm dark:bg-paper-dark-warm px-4 py-3 flex items-start gap-3"
      >
        <span aria-hidden className="text-xl">
          📱
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Instalar IGM no seu iPhone
          </p>
          <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5">
            Toque em <span aria-hidden>⬆️</span> (compartilhar) → <strong>Adicionar à Tela de Início</strong>
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dispensar"
          data-testid="pwa-install-banner-dismiss"
          className="text-ink-2 dark:text-ink-dark-2 hover:text-ink-1 dark:hover:text-ink-dark-1 text-lg leading-none"
        >
          ×
        </button>
      </div>
    );
  }

  // Android/desktop com beforeinstallprompt disponível.
  if (!installEvent) return null;

  return (
    <div
      role="region"
      aria-label="Instalar IGM"
      data-testid="pwa-install-banner"
      className="rounded-xl border border-line dark:border-line-dark bg-paper-warm dark:bg-paper-dark-warm px-4 py-3 flex items-center gap-3"
    >
      <span aria-hidden className="text-xl">
        📲
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
          Instalar IGM no seu celular
        </p>
        <p className="text-xs text-ink-2 dark:text-ink-dark-2 mt-0.5">
          Acesso rápido pela tela inicial + notificações nativas
        </p>
      </div>
      <Button size="sm" onClick={handleInstall} data-testid="pwa-install-banner-install">
        Instalar
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={handleDismiss}
        data-testid="pwa-install-banner-dismiss"
      >
        Depois
      </Button>
    </div>
  );
}
